import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { generateAddedValueFormName } from '../../../../helpers/form-added-value-name-generator';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { RmsSpecificationUpdateItemAction } from '../../../../actions/rms-specification.actions';
import { getRmsRecipeSpecifications, getRmsRecipeSpecificationsIsModify } from '../../../../selectors';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { getMostRecentContract } from '@ordersnproducts/modules/shared/utils/get-most-recent-contract-function';
import { Features } from '@ordersnproducts/features';
import { getOpenPlatformProducts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';

/**
 * Used to reset selection for contract and value addeds when product changed,
 * so invalid VAP combinations can be discarded.
 * Triggers 'change' in contract value for order with multiple products.
 */
@Injectable()
export class ResetVapsAndContractOPCommandService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private featureToggle: FeatureToggleService,
  ) {}

  onFieldChanged(value: any, formGroup: UntypedFormGroup, form: ConfigurableFormComponent, config: FieldConfig): void {
    const idFormValue = formGroup.get('id').value;
    const contractFormControl = formGroup.get(SpecFieldNames.CONTRACT_SELECT);

    // CURRENT VAPS QUANTITY FOR COUNTRIES ON COMMAND EXPERIENCE

    this.store
      .select(getRmsRecipeSpecifications)
      .pipe(take(1), withLatestFrom(this.store.select(getOpenPlatformProducts)))
      .subscribe(([forms, availableProducts]) => {
        const changeRecipe = forms.find((recipeForm) => recipeForm.id === idFormValue);
        if (changeRecipe && changeRecipe.values.productSelect !== value) {
          const vapsQuantity = 4;
          for (let i = 0; i < vapsQuantity; i++) {
            const valueAddedControl = formGroup.get(generateAddedValueFormName(i));
            const controlName = generateAddedValueFormName(i);

            if (!!valueAddedControl && !!valueAddedControl.value) {
              this.resetControl(valueAddedControl);
              let values = form.form.getRawValue();
              values = { ...values, [controlName]: undefined };
              // NEEDED FOR RESETING STATE VALUES
              this.store.dispatch(new RmsSpecificationUpdateItemAction({ id: form.data.id, values }));
            }
          }

          const isParentForm = forms[0].id === idFormValue;
          if (idFormValue === 0 || isParentForm) {
            this.resetControl(contractFormControl);
            const removeContractFieldEnabled = this.featureToggle.isFeatureEnabled(Features.RemoveContractFieldEnabled);

            if (removeContractFieldEnabled) {
              const product = availableProducts.find(
                (availableProd) =>
                  availableProd.productCode.replace(/^0+/, '') ===
                  formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value,
              );
              product.contracts = getMostRecentContract(product.contracts);
              if (product.contracts.length > 0) {
                const defaultValue = _safe(() => product.contracts[0].contractCode.replace(/^0+/, ''));
                formGroup.get(SpecFieldNames.CONTRACT_SELECT).setValue(defaultValue);
              }
            }
          } else {
            this.resetControl(contractFormControl, contractFormControl.value);
          }
        }
      });
  }

  resetControl(control: AbstractControl, value?: string) {
    control.setValue(value, { emitEvent: true });
    control.setErrors(null);
  }
}

/**
 * Used to clear data for VAPS when contract changed
 * (and has no VAPs to overwrite current values)
 */
@Injectable()
export class ResetVapsValueOPCommandService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value: any, formGroup: UntypedFormGroup, form: ConfigurableFormComponent, config: FieldConfig): void {
    this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        filter((recipe) => recipe.length > 0),
      )
      .subscribe((recipe) => {
        const formId = _safe(() => formGroup.get(SpecFieldNames.ID).value);
        const recipeChange = recipe.find((recipeForm) => recipeForm.id === formId);
        if (recipeChange && recipeChange.values.contractSelect !== value) {
          const vapsQuantity = 4;
          for (let i = 0; i < vapsQuantity; i++) {
            const valueAddedControl = formGroup.get(generateAddedValueFormName(i));
            const controlName = generateAddedValueFormName(i);

            if (!!valueAddedControl && !!valueAddedControl.value) {
              valueAddedControl.setValue(undefined, { emitEvent: true });
              valueAddedControl.setErrors(null);
              valueAddedControl.markAsPristine();
              valueAddedControl.markAsUntouched();
              valueAddedControl.updateValueAndValidity();

              let values = form.form.getRawValue();
              values = { ...values, [controlName]: undefined };
              this.store.dispatch(new RmsSpecificationUpdateItemAction({ id: form.data.id, values }));
            }
          }
        }
      });
  }
}

@Injectable()
export class ResetRepeatVapsAndContractOPCommandService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private featureToggle: FeatureToggleService,
  ) {}

  onFieldChanged(value: any, formGroup: UntypedFormGroup, form: ConfigurableFormComponent, config: FieldConfig): void {
    const idFormValue = formGroup.get('id').value;

    this.store
      .select(getRmsRecipeSpecificationsIsModify)
      .pipe(
        // FILTERS EVENT FOR FIRST ITERATION WHEN REPEATING AN ORDER
        filter(
          (data) =>
            !!data.forms.find((item) => item.values.id === idFormValue) &&
            data.forms.find((item) => item.values.id === idFormValue).values[SpecFieldNames.PRODUCT_SELECTION] !==
              value,
        ),
        withLatestFrom(this.store.select(getOpenPlatformProducts)),
        take(1),
      )
      .subscribe(([data, availableProducts]) => {
        // CURRENT VAPS QUANTITY FOR COUNTRIES IN COMMAND EXPERIENCE
        const vapsQuantity = 4;
        for (let i = 0; i < vapsQuantity; i++) {
          const valueAddedControl = formGroup.get(generateAddedValueFormName(i));
          const controlName = generateAddedValueFormName(i);

          if (!!valueAddedControl && !!valueAddedControl.value) {
            this.resetControl(valueAddedControl);
            let values = form.form.getRawValue();
            values = { ...values, [controlName]: undefined };
            // NEEDED FOR RESETING STATE VALUES
            this.store.dispatch(new RmsSpecificationUpdateItemAction({ id: form.data.id, values }));
          }
        }

        const isParentForm = idFormValue === 0 || data.forms[0].id === idFormValue;
        const parentControlValue = data.forms[0].values[SpecFieldNames.CONTRACT_SELECT];
        const contractFormControl = formGroup.get(SpecFieldNames.CONTRACT_SELECT);
        if (isParentForm && !data.isModify) {
          this.resetControl(contractFormControl);
          this.resetControl(contractFormControl);
          const removeContractFieldEnabled = this.featureToggle.isFeatureEnabled(Features.RemoveContractFieldEnabled);

          if (removeContractFieldEnabled) {
            const product = availableProducts.find(
              (availableProd) =>
                availableProd.productCode.replace(/^0+/, '') === formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value,
            );
            if (!!product) {
              product.contracts = getMostRecentContract(product.contracts);
              if (product.contracts.length > 0) {
                const defaultValue = _safe(() => product.contracts[0].contractCode.replace(/^0+/, ''));
                formGroup.get(SpecFieldNames.CONTRACT_SELECT).setValue(defaultValue);
              }
            }
          }
        } else {
          this.resetControl(contractFormControl, parentControlValue);
        }
      });
  }

  resetControl(control: AbstractControl, value?: string) {
    control.setValue(value, { emitEvent: true });
    control.setErrors(null);
  }
}
